import { useFeatureIsOn } from "@growthbook/growthbook-react";
import type dropin from "braintree-web-drop-in";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { useSelector } from "react-redux";
import { Store as ReduxStore } from "redux";

import config from "config";
import { FeatureFlags } from "gyg_common/modules/FeatureFlags/constants";

import {
  AnalyticsAction,
  BraintreeReduxActions,
  CheckoutAction,
  CheckoutModels,
  locales,
  RootState,
  SentryLoggerInstance,
} from "../index";

interface BrainTreeProps {
  clientToken: string;
  orderTotal: string;
  currency: string;
  customer: CheckoutModels.Customer;
  orderId: string;
  isExistingPayMethod: boolean;
  existingPayType?: FetchDropInResult | undefined;
  merchantName: string;
  isSavePayment: boolean;
  includeCutlery: boolean;
  partPayment?: CheckoutModels.Payment;
  analyticsPaymentType: (paymentType: string) => void;
  initPayment: (payload: CheckoutModels.PaymentPayload) => void;
}

interface FetchDropInResult {
  nonce: string;
  type: string;
  deviceData: string;
}

interface WebOrAppBrainTree {
  appInitBrainTree?: (props: BrainTreeProps) => void;
  appPreviousPayMethod?: FetchDropInResult;
  webBrainTreeInstance?: dropin.Dropin;
  webIsReinitBrainTreeRef?: React.MutableRefObject<boolean>;
  webSetBrainTreeInstance?: React.Dispatch<
    React.SetStateAction<dropin.Dropin | undefined>
  >;
}

export const useInitPayment = (
  store: ReduxStore,
  orderTotal: number,
  braintree: WebOrAppBrainTree
) => {
  const { t } = useTranslation();
  const { isAuthSuccess } = useSelector((s: RootState) => s.login);
  const { profile } = useSelector((s: RootState) => s.user);
  const { activeReward, loyalty, redeemDollars } = useSelector(
    (state: RootState) => state.loyalty
  );
  const { getOrderResponse, includeCutlery, currentOrderId } = useSelector(
    (s: RootState) => s.order
  );
  const { paymentPayload: savedPaymentPayload } = useSelector(
    (s: RootState) => s.braintree
  );
  const { clientTokenResponse, isSavePayment } = useSelector(
    (s: RootState) => s.checkout
  );
  const { selectedStore } = useSelector((s: RootState) => s.store);
  const { firstName, lastName, phoneNumber, email } = useSelector(
    (s: RootState) => s.guest
  );
  const dispatch = store.dispatch;

  const trackPaymentType = useCallback(
    (paymentType: string) => {
      dispatch(AnalyticsAction.setPaymentType(paymentType));
    },
    [dispatch]
  );

  const isAsyncCheckoutMode = useFeatureIsOn(
    FeatureFlags.ASYNC_CHECKOUT as string
  );

  const performCheckout = useCallback(
    (paymentPayload: CheckoutModels.PaymentPayload) => {
      if (selectedStore) {
        dispatch(
          BraintreeReduxActions.savePaymentPayload({
            paymentPayload,
            store: selectedStore,
          })
        );
      }

      dispatch(
        (isAuthSuccess
          ? CheckoutAction.initPaymentAuthorized
          : CheckoutAction.initPayment)({
          ...paymentPayload,
          async: isAsyncCheckoutMode,
        })
      );
    },
    [dispatch, isAsyncCheckoutMode, isAuthSuccess, selectedStore]
  );

  const initPaymentForWeb = useCallback(
    (
      payment: CheckoutModels.Payment,
      customer: CheckoutModels.Customer,
      orderId?: string
    ) => {
      const instance = braintree.webBrainTreeInstance;
      if (instance) {
        instance.requestPaymentMethod((braintreeError, payload) => {
          if (braintreeError) {
            SentryLoggerInstance.sentryBraintreeError(braintreeError);
          }
          //proceeds with payment
          if (payload) {
            payment.braintree = {
              checkoutType: 0,
              nonce: payload.nonce,
              amount: orderTotal.toFixed(2),
              storeCard: isSavePayment,
              paymentToken: clientTokenResponse.clientToken,
              bTAFT: payload.deviceData || "",
            };
            if (orderId) {
              const newPayload: CheckoutModels.PaymentPayload = {
                id: orderId,
                customer,
                payment,
                addCutlery: includeCutlery,
              };
              dispatch(AnalyticsAction.setPaymentType(payload.type));
              performCheckout(newPayload);
            } else {
              SentryLoggerInstance.sentryBraintreeError(
                new Error("PaymentContainer:initBrainTree: order ID not found")
              );
            }
          } else {
            //clear all instance and get new client token
            if (braintree.webIsReinitBrainTreeRef.current) {
              return;
            }
            braintree.webIsReinitBrainTreeRef.current = true;
            instance.teardown();
            braintree.webSetBrainTreeInstance(undefined);
          }
        });
      } else if (currentOrderId) {
        dispatch(
          CheckoutAction.getClientToken({
            orderId: currentOrderId,
          })
        );
      }
    },
    [
      braintree,
      clientTokenResponse?.clientToken,
      currentOrderId,
      dispatch,
      includeCutlery,
      isSavePayment,
      orderTotal,
      performCheckout,
    ]
  );

  const initPaymentCb = useCallback(
    (existingPayMethod?: boolean) => {
      const payment: CheckoutModels.Payment = {};
      const customer: CheckoutModels.Customer = {
        firstName: isAuthSuccess ? profile?.firstName ?? "" : firstName,
        lastName: isAuthSuccess ? profile?.lastName ?? "" : lastName,
        emailAddress: isAuthSuccess ? profile?.email ?? "" : email,
        phoneNumber: isAuthSuccess ? profile?.phoneNumber ?? "" : phoneNumber,
      };
      const orderId = getOrderResponse?.orderId;

      // logged in users only
      if (isAuthSuccess && redeemDollars && loyalty?.cardNumber) {
        payment.gygDollar = {
          cardNumber: loyalty.cardNumber,
          amount: redeemDollars,
        };
      }

      // logged in users only
      if (isAuthSuccess && activeReward?.userCouponCode) {
        payment.coupon = {
          code: activeReward.userCouponCode,
        };
      }

      if (savedPaymentPayload) {
        dispatch(
          (isAuthSuccess
            ? CheckoutAction.initPaymentAuthorized
            : CheckoutAction.initPayment)({
            ...savedPaymentPayload,
            async: isAsyncCheckoutMode,
          })
        );
      } else if (clientTokenResponse?.clientToken !== "" && orderTotal > 0) {
        // handle web Braintree payment
        if (Platform.OS === "web") {
          initPaymentForWeb(payment, customer, orderId);
        } else {
          // handle app Braintree payment
          const brainTreeProps: BrainTreeProps = {
            merchantName: t("CheckoutPayment:storeLabel"),
            clientToken: clientTokenResponse.clientToken,
            orderTotal: orderTotal.toFixed(2),
            currency: config.version === locales.AU ? "AUD" : "USD",
            customer: customer,
            orderId: orderId ?? currentOrderId ?? "",
            isExistingPayMethod: existingPayMethod,
            existingPayType: braintree.appPreviousPayMethod ?? undefined,
            isSavePayment: isSavePayment,
            partPayment: payment,
            includeCutlery,
            analyticsPaymentType: trackPaymentType,
            initPayment: performCheckout,
          };
          braintree.appInitBrainTree(brainTreeProps);
        }
        // when user pays completely with GyG dollars
      } else if (payment && orderTotal === 0) {
        const paymentPayload: CheckoutModels.PaymentPayload = {
          id: orderId ?? currentOrderId ?? "",
          customer: customer,
          payment: payment,
          addCutlery: includeCutlery,
        };
        dispatch(
          (isAuthSuccess
            ? CheckoutAction.initPaymentAuthorized
            : CheckoutAction.initPayment)({
            ...paymentPayload,
            async: isAsyncCheckoutMode,
          })
        );
        // defensive scenario where there is an order but no client token is available
      } else if (
        !clientTokenResponse?.clientToken &&
        getOrderResponse?.orderId
      ) {
        dispatch(
          CheckoutAction.getClientToken({
            orderId: getOrderResponse.orderId,
          })
        );
      }
    },
    [
      isAuthSuccess,
      profile?.firstName,
      profile?.lastName,
      profile?.email,
      profile?.phoneNumber,
      firstName,
      lastName,
      email,
      phoneNumber,
      getOrderResponse?.orderId,
      redeemDollars,
      loyalty?.cardNumber,
      activeReward?.userCouponCode,
      savedPaymentPayload,
      clientTokenResponse?.clientToken,
      orderTotal,
      dispatch,
      braintree,
      currentOrderId,
      isSavePayment,
      includeCutlery,
      performCheckout,
      t,
      trackPaymentType,
      initPaymentForWeb,
      isAsyncCheckoutMode,
    ]
  );

  return initPaymentCb;
};
